import React, { useState, useEffect } from 'react';
import ProductCard from '../../components/ProductCard';
import '../../css/components/ProductCategories/ProductCategories.css'
import filterIcon from "../../images/filters.png"
import filterIconMobile from "../../images/filterWhite.png"
import closeIcon from "../../images/close.png"
import closeIconWhite from "../../images/closeWhite.png"
import nextArrow from "../../images/nextArrow.png"
import Pagination from '../Pagination';

const getDiscountPercent = (product) => {
    const price = Number(product?.price);
    const salePrice = Number(product?.sale_price);
    if (!price || price <= 0) return 0;
    if (!salePrice || salePrice <= 0 || salePrice >= price) return 0;
    return ((price - salePrice) / price) * 100;
};

const LayoutSearch = ({ products, searchQuery, totalResults, serverPaginated = false }) => {
    
    const productsStock = products;
    const [sortedProducts, setSortedProducts] = useState(products);
    const [isFilterModalActive, setIsFilterModalActive] = useState(false);
    const [isOrderByModalActive, setIsOrderByModalActive] = useState(false);
    const [activeFilter, setActiveFilter] = useState('Filtros');
    const [filters, setFilters] = useState({});
    const [pendingFilters, setPendingFilters] = useState({});
    const [sortOption, setSortOption] = useState('');
    const [pendingSortOption, setPendingSortOption] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);

    // Cuando serverPaginated es true, usar todos los productos directamente (ya vienen paginados del servidor)
    const indexOfLastItem = serverPaginated ? sortedProducts.length : currentPage * itemsPerPage;
    const indexOfFirstItem = serverPaginated ? 0 : indexOfLastItem - itemsPerPage;
    const currentProducts = serverPaginated ? sortedProducts : sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = serverPaginated ? 1 : Math.ceil(sortedProducts.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        if (!serverPaginated) {
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
        }
    }, [currentPage, serverPaginated]);

    const handleFilterChange = (attributeName, option) => {
        setPendingFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            if (!newFilters[attributeName]) {
                newFilters[attributeName] = [];
            }
    
            if (newFilters[attributeName].includes(option)) {
                newFilters[attributeName] = newFilters[attributeName].filter(item => item !== option);
            } else {
                newFilters[attributeName].push(option);
            }
    
            return newFilters;
        });
    };
    
    const combinedAttributes = products.reduce((acc, product) => {
        product.attributes.forEach(attr => {
            const existingAttr = acc.find(a => a.name === attr.name);
            if (existingAttr) {
                existingAttr.options = [...new Set([...existingAttr.options, ...attr.options])];
            } else {
                acc.push({ ...attr });
            }
        });
        return acc;
    }, []);
    
    
    const applyFilters = () => {
        setFilters(pendingFilters);
    
        let filtered = products;
    
        Object.keys(pendingFilters).forEach(attribute => {
            const options = pendingFilters[attribute];
            if (options.length > 0) {
                filtered = filtered.filter(product =>
                    product.attributes.some(attr =>
                        attr.name === attribute && options.some(option => attr.options.includes(option))
                    )
                );
            }
        });
    
        filtered.sort((a, b) => {
            const aStock = a.stock_quantity ?? 0;
            const bStock = b.stock_quantity ?? 0;
    
            if (aStock > 0 && bStock <= 0) return -1;
            if (aStock <= 0 && bStock > 0) return 1;
            return 0;
        });
    
        setSortedProducts(filtered);
        setIsFilterModalActive(false);
    };
    
    const handleSortChange = (option) => {
        setPendingSortOption(option);
    };
    const applySort = () => {
        setSortOption(pendingSortOption);

        let sorted = [...sortedProducts];
        switch (pendingSortOption) {
            case 'recent':
                sorted.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
                break;
            case 'bestSelling':
                sorted.sort((a, b) => b.total_sales - a.total_sales);
                break;
            case 'priceHighToLow':
                sorted.sort((a, b) => b.sale_price - a.sale_price);
                break;
            case 'priceLowToHigh':
                sorted.sort((a, b) => a.sale_price - b.sale_price);
                break;
            case 'alphabetical':
                sorted.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'discountHighToLow':
                sorted.sort((a, b) => getDiscountPercent(b) - getDiscountPercent(a));
                break;
            case 'discountLowToHigh':
                sorted.sort((a, b) => getDiscountPercent(a) - getDiscountPercent(b));
                break;
            default:
                break;
        }
        sorted.sort((a, b) => {
            const aStock = a.stock_quantity ?? 0;
            const bStock = b.stock_quantity ?? 0;

            if (aStock > 0 && bStock <= 0) return -1;
            if (aStock <= 0 && bStock > 0) return 1;

            return 0;
        });
        setSortedProducts(sorted);
        setIsFilterModalActive(false);
        setIsOrderByModalActive(false);
    };

    const clearFilters = () => {
        setPendingFilters({});
        setFilters({});
        setSortedProducts(products);
    };
    
    const clearSort = () => {
        setPendingSortOption('');
        setSortOption('');
        setSortedProducts(products);
    };
    
    useEffect(() => {
        if (!products) return;

        // En búsqueda (serverPaginated) el backend ya ordena por relevancia:
        // coincidencia con el nombre primero, y como desempate stock y luego
        // ventas. Hay que respetar ESE orden y no re-ordenar en el cliente, o si
        // no los más vendidos se saltan la fila y un agotado puede subir.
        if (serverPaginated) {
            setSortedProducts(products);
            return;
        }

        const sorted = [...products].sort((a, b) => {
            const aStock = a.stock_quantity == null ? 0 : a.stock_quantity;
            const bStock = b.stock_quantity == null ? 0 : b.stock_quantity;

            if (aStock > 0 && bStock <= 0) return -1;
            if (aStock <= 0 && bStock > 0) return 1;

            if (aStock > 0 && bStock > 0) {
                return (b.total_sales ?? 0) - (a.total_sales ?? 0);
            }

            return 0;
        });
        setSortedProducts(sorted);
    }, [products, serverPaginated]);
    
    const [currentItems, setCurrentItems] = useState([]);
    const [modalFilterBy, setModalFilterBy] = useState(false);

    const maxPageNumbersToShow = 5; 

    useEffect(() => {
        if (!serverPaginated) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentPage, serverPaginated]);
    
    const getPageNumbersToShow = () => {
         const totalPagesCalc = Math.ceil(sortedProducts.length / itemsPerPage);
         const halfMaxPageNumbersToShow = Math.floor(maxPageNumbersToShow / 2);
 
         let startPage = Math.max(1, currentPage - halfMaxPageNumbersToShow);
         let endPage = Math.min(totalPagesCalc, startPage + maxPageNumbersToShow - 1);
 
         if (startPage === 1) {
             endPage = Math.min(maxPageNumbersToShow, totalPagesCalc);
         }
         else if (endPage === totalPagesCalc) {
             startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
         }
 
         const pageNumbersToShow = [];
         for (let i = startPage; i <= endPage; i++) {
             pageNumbersToShow.push(i);
         }
 
         return pageNumbersToShow;
    };
 
    const pageNumbersToShow = getPageNumbersToShow();
 
    const renderPageNumbers = pageNumbersToShow.map(number => (
         <button 
             key={number} 
             onClick={() => currentPage !== number && setCurrentPage(number)}
             className={currentPage === number ? "active" : ""}
             disabled={currentPage === number}
             style={{ cursor: currentPage === number ? "default" : "pointer" }}
         >
             {number}
         </button>
    ));

    useEffect(() => {
        if (sortedProducts && !serverPaginated) {
            const idxOfLastItem = currentPage * itemsPerPage;
            const idxOfFirstItem = idxOfLastItem - itemsPerPage;
            const newCurrentItems = sortedProducts.slice(idxOfFirstItem, idxOfLastItem);
            setCurrentItems(newCurrentItems);
        } else if (sortedProducts && serverPaginated) {
            setCurrentItems(sortedProducts);
        }
    }, [currentPage, sortedProducts, itemsPerPage, serverPaginated]);

    // Construir el texto de breadcrumbs
    const breadcrumbText = searchQuery
        ? `Inicio / Búsqueda: "${searchQuery}" (${totalResults != null ? totalResults : products.length} resultados)`
        : 'Inicio';
    
    if (productsStock && productsStock.length > 0) {
        const pageNumbers = [];
        for (let i = 1; i <= Math.ceil(sortedProducts.length / itemsPerPage); i++) {
            pageNumbers.push(i);
        }
        let paginationItems;
        if (pageNumbers.length <= 5) {
          paginationItems = pageNumbers.map(number => (
            <button key={number} onClick={() => setCurrentPage(number)}>
              {number}
            </button>
          ));
        } else { 
          paginationItems = (
            <>
              {currentPage > 4 && <div className='productsCategories__right--jumpPage-pageNumber'>...</div>}
              {currentPage > 1 && (
                 <div onClick={() => setCurrentPage(currentPage - 1)} className="productsCategories__right--jumpPage-pageNumber pageActive">
                    {currentPage - 1}
                </div>
              )}
              <div className="productsCategories__right--jumpPage-pageNumber pageDisabled" disabled>{currentPage}</div>
              {currentPage < pageNumbers.length - 1 && (
                <div onClick={() => setCurrentPage(currentPage + 1)} className="productsCategories__right--jumpPage-pageNumber pageActive">
                    {currentPage + 1}
                </div>
              )}
              {currentPage < pageNumbers.length - 2 && <div className='productsCategories__right--jumpPage-pageNumber'>...</div>}
              <div className='productsCategories__right--jumpPage-pageNumber pageActive' onClick={() => setCurrentPage(pageNumbers.length)}>
                {pageNumbers.length}
              </div>
            </>
          );
        }
        return (
            <div className="Content__productCategories">
                <div style={{maxWidth: "100%"}} className="">
                    <div className="Content__productCategories--grid">
                        <div className="Content__productCategories--heading">
                            <div className="Content__productCategories--breadcrumbs">
                                <span>{breadcrumbText}</span>
                            </div>
                            <div className="Content__productCategories--filtersGroup">
                                <div className="Content__productCategories--filtersContainer">
                                    <div className="Content__productCategories--filters" onClick={() => setIsFilterModalActive(true)}>
                                        <h4>Filtrar por</h4>
                                        <img src={filterIcon} alt="" className="desktopIconFilter" />
                                        <img src={filterIconMobile} alt="" className="mobileIconFilter" />
                                    </div>
                                    {isFilterModalActive && (
                                        <div 
                                            className={`Content__productCategories--filtersModal ${isFilterModalActive ? 'filtersModalActive' : ''}`}
                                            onClick={(e) => {
                                                if (e.target === e.currentTarget) {
                                                    setIsFilterModalActive(false);
                                                }
                                            }}
                                        >
                                            <div className="Content__productCategories--filters-Heading">
                                                <h4>Filtros</h4>
                                                <img src={closeIcon} className="desktopCloseIcon" alt="" onClick={() => setIsFilterModalActive(false)} />
                                                <img src={closeIconWhite} className="mobileCloseIcon" alt="" onClick={() => setIsFilterModalActive(false)} />
                                            </div>
                                            <div className="Content__productCategories--filters-body">
                                                <div className="Content__productCategories--filters-content">
                                                    {combinedAttributes.map(attribute => (
                                                        <div style={{marginTop: "20px"}} key={attribute.id}>
                                                            <h4>{attribute.name}</h4>
                                                            <ul>
                                                                {attribute.options.map(option => (
                                                                    <li key={option}>
                                                                        <label className="customCheckboxContainer">
                                                                            <input
                                                                                type="checkbox"
                                                                                onChange={() => handleFilterChange(attribute.name, option)}
                                                                                checked={pendingFilters[attribute.name]?.includes(option) || false}
                                                                            />
                                                                            {option}
                                                                            <span className="checkmark"></span>
                                                                        </label>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="Content__productCategories--filters-footer">
                                                <button onClick={clearFilters}>Limpiar</button>
                                                <button id="aplicarButton" onClick={applyFilters}>Aplicar</button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="Content__productCategories--orderBy">
                                    <label htmlFor="orderBy" onClick={() => setIsOrderByModalActive(true)}>
                                        Ordenar Por:
                                        <strong className="desktopIconFilter">
                                            {sortOption === 'recent' && 'Recien agregados'}
                                            {sortOption === 'bestSelling' && 'Más vendidos'}
                                            {sortOption === 'priceHighToLow' && 'Precio más alto al más bajo'}
                                            {sortOption === 'priceLowToHigh' && 'Precio más bajo al más alto'}
                                            {sortOption === 'alphabetical' && 'Abecedario A - Z'}
                                            {sortOption === 'discountHighToLow' && 'Mayor descuento'}
                                            {sortOption === 'discountLowToHigh' && 'Menor descuento'}
                                            {!sortOption && 'Seleccionar'}
                                        </strong>
                                        <img src={filterIconMobile} alt="" className="mobileIconFilter" />
                                    </label>
                                    <div 
                                        className={`Content__productCategories--filtersModal ${isOrderByModalActive ? 'filtersModalActive' : ''}`}
                                        onClick={(e) => {
                                            if (e.target === e.currentTarget) {
                                                setIsOrderByModalActive(false);
                                            }
                                        }}
                                    >
                                        <div className="Content__productCategories--filters-Heading">
                                            <h4>Ordenar por</h4>
                                            <img
                                                src={closeIcon}
                                                className="desktopCloseIcon"
                                                alt=""
                                                onClick={() => setIsOrderByModalActive(false)}
                                            />
                                            <img
                                                src={closeIconWhite}
                                                className="mobileCloseIcon"
                                                alt=""
                                                onClick={() => setIsOrderByModalActive(false)}
                                            />
                                        </div>
                                        <div className="Content__productCategories--filters-body">
                                            <div className="Content__productCategories--filters-content">
                                                <ul>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'bestSelling'}
                                                                onChange={() => handleSortChange('bestSelling')}
                                                            />
                                                            Más vendidos
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'recent'}
                                                                onChange={() => handleSortChange('recent')}
                                                            />
                                                            Recien agregados
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'priceHighToLow'}
                                                                onChange={() => handleSortChange('priceHighToLow')}
                                                            />
                                                            Precio más alto al más bajo
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'priceLowToHigh'}
                                                                onChange={() => handleSortChange('priceLowToHigh')}
                                                            />
                                                            Precio más bajo al más alto
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'alphabetical'}
                                                                onChange={() => handleSortChange('alphabetical')}
                                                            />
                                                            Abecedario A - Z
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'discountHighToLow'}
                                                                onChange={() => handleSortChange('discountHighToLow')}
                                                            />
                                                            Mayor descuento
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <label className="customCheckboxContainer">
                                                            <input
                                                                type="radio"
                                                                name="sort"
                                                                checked={pendingSortOption === 'discountLowToHigh'}
                                                                onChange={() => handleSortChange('discountLowToHigh')}
                                                            />
                                                            Menor descuento
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="Content__productCategories--filters-footer">
                                            <button onClick={clearSort}>Limpiar</button>
                                            <button id="aplicarButton" onClick={applySort}>Aplicar</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="Content__productCategories--cards">
                            {currentProducts.map((product)=> {
                                return (
                                    <ProductCard key={product.id} product={product} addSource="search"/>
                                )
                            })}
                            <div className={`overlayProducts ${isFilterModalActive ? 'overlayActive' : ''}`} onClick={() => { setIsFilterModalActive(false); }}></div>
                            <div className={`overlayProducts ${isOrderByModalActive ? 'overlayActive' : ''}`} onClick={() => { setIsOrderByModalActive(false); }}></div>
                        </div>

                        {/* Paginador interno solo cuando NO hay paginación del servidor */}
                        {!serverPaginated && totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                leftEdgePages={2}
                                rightEdgePages={2}
                                showLeftEllipsis={true}
                                showRightEllipsis={true}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default LayoutSearch;